import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";
import { fetchHolders, fetchLargeTrades, fetchMarketsByCondition, type OutcomeHolders } from "@/api/polymarketApi";
import { fetchSupersetCharts, fetchSupersetStatus } from "@/api/supersetApi";
import { fetchMarket, getJson } from "@/api/terminalApi";
import type { AlertsResponse } from "@/types/terminal";
import { shortAddr } from "../../wallets/lib/format";
import { useLive } from "../lib/data";
import { toFlaggedRow } from "../lib/flagged";
import { FLAGS_REBUILDING } from "../lib/flags";
import { ago, cents, centsDelta, count, isoDay, stamp, toneClass, usd } from "../lib/format";
import { marketUrl, normalizeMarket, type DeskMarket } from "../lib/markets";
import { closeMarket, useParam } from "../lib/nav";
import { useApiStatus } from "../lib/status";
import { useWatchlist } from "../lib/watchlist";
import { holderSplit, tradeUsd } from "../lib/whales";
import { ScoreWhy } from "../views/ScoreWhy";
import { ApiDown, Empty, OutcomeTag, Section, SkeletonRows, SourceError, Stat, StatGrid, Tabs, Toggle, WalletLink } from "./Bits";

const ChartCard = lazy(() => import("../../dashboard/components/ChartCard").then((m) => ({ default: m.ChartCard })));

type Tab = "overview" | "holders" | "charts";

const TABS: { id: Tab; label: string }[] = [
	{ id: "overview", label: "overview" },
	{ id: "holders", label: "holders" },
	{ id: "charts", label: "charts" },
];

// PolyGains per-market analytics, in reading order. Titles come from the API.
const CHART_IDS = [43, 41, 42, 50, 51, 52, 36, 37, 38, 35];

const SPLIT_COLOR = ["bg-info", "bg-up", "bg-warn"];

const fill = (name: string, i: number) => {
	const n = name.toLowerCase();
	return n === "yes" ? "bg-up" : n === "no" ? "bg-dn" : i === 0 ? "bg-fg" : "bg-mute";
};

type OutcomeStat = { tokenId?: unknown; mean: number | null; stdDev: number | null; p95: number | null };

/** A two-outcome market is one whole, so it reads as one split bar. */
function SplitBar({ market }: { market: DeskMarket }) {
	const [a, b] = market.outcomes;
	if (!a || !b) return null;
	const total = (a.price ?? 0) + (b.price ?? 0) || 1;
	return (
		<div className="px-2 py-2">
			<div className="flex items-baseline justify-between gap-3">
				<span className="min-w-0 truncate text-data text-fg">
					{a.name} <b className="num text-head text-bright">{cents(a.price)}</b>
				</span>
				<span className="min-w-0 truncate text-right text-data text-fg">
					<b className="num text-head text-bright">{cents(b.price)}</b> {b.name}
				</span>
			</div>
			<div className="mt-1 flex h-2 gap-0.5" role="img" aria-label={`${a.name} ${cents(a.price)}, ${b.name} ${cents(b.price)}`}>
				<span className={fill(a.name, 0)} style={{ width: `${((a.price ?? 0) / total) * 100}%` }} />
				<span className={`flex-1 ${fill(b.name, 1)}`} />
			</div>
		</div>
	);
}

function OutcomeRows({ market }: { market: DeskMarket }) {
	return (
		<>
			{market.outcomes.map((o, i) => (
				<div key={o.name} className="flex h-(--row-h) items-center gap-2 border-b border-line/50 px-2 text-body last:border-b-0">
					<span className="w-24 shrink-0 truncate text-fg" title={o.name}>{o.name}</span>
					<span className="h-2 min-w-0 flex-1 bg-line">
						<span className={`block h-full ${fill(o.name, i)}`} style={{ width: `${(o.price ?? 0) * 100}%` }} />
					</span>
					<span className="num w-12 shrink-0 text-right font-bold text-bright">{cents(o.price)}</span>
				</div>
			))}
		</>
	);
}

function Outcomes({ market, conditionId }: { market: DeskMarket; conditionId: string }) {
	const api = useApiStatus();
	const { data } = useSWR(api.state === "up" ? ["api:market", conditionId] : null, () => fetchMarket(conditionId));
	const stats = new Map(((data?.outcomes as OutcomeStat[] | undefined) ?? []).map((o) => [String(o.tokenId), o]));
	return (
		<Section title="Outcomes" note="price = implied chance">
			{market.outcomes.length === 2 ? <SplitBar market={market} /> : <OutcomeRows market={market} />}
			{stats.size ? (
				<table className="tbl border-t border-line">
					<thead>
						<tr>
							<th>Trade size</th>
							<th className="r">Mean</th>
							<th className="r">Std dev</th>
							<th className="r">p95</th>
						</tr>
					</thead>
					<tbody>
						{market.outcomes.map((o) => {
							const s = o.tokenId ? stats.get(o.tokenId) : undefined;
							return (
								<tr key={o.name}>
									<td className="text-fg">{o.name}</td>
									<td className="r num">{usd(s?.mean)}</td>
									<td className="r num">{usd(s?.stdDev)}</td>
									<td className="r num">{usd(s?.p95)}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			) : null}
		</Section>
	);
}

const TRADE_GRID = "grid grid-cols-[2rem_2.25rem_minmax(0,1fr)_minmax(0,6rem)_3.5rem] items-center gap-2 px-2";

function RecentLarge({ conditionId }: { conditionId: string }) {
	const { data, error, isLoading } = useLive(["pm:trades:market", conditionId], (signal) =>
		fetchLargeTrades({ minUsd: 1000, limit: 12, condition: conditionId }, signal),
	);
	return (
		<Section title="Recent large trades" note="$1K and over · Polymarket data-api">
			{error && !data ? (
				<SourceError source="Polymarket (data-api)" />
			) : isLoading ? (
				<SkeletonRows rows={6} cols={3} />
			) : !data?.length ? (
				<Empty>No trades of this size.</Empty>
			) : (
				<>
					<div className={`${TRADE_GRID} h-6 border-b border-line text-label uppercase tracking-wide text-dim`}>
						<span>Age</span>
						<span>Side</span>
						<span>Wallet</span>
						<span className="text-right">At</span>
						<span className="text-right">Size</span>
					</div>
					{data.map((t) => (
						<div key={`${t.transaction_hash}:${t.proxy_wallet}:${t.size}`} className={`${TRADE_GRID} h-(--row-h) border-b border-line/50 text-body last:border-b-0 hover:bg-raised`}>
							<span className="num text-dim">{ago(t.timestamp)}</span>
							<span className={t.side === "BUY" ? "text-up" : "text-dn"}>{t.side}</span>
							<span className="truncate"><WalletLink address={t.proxy_wallet} label={t.name || t.pseudonym} /></span>
							<span className="num truncate text-right text-dim" title={t.outcome}>{t.outcome} {cents(t.price)}</span>
							<span className="num text-right font-bold text-bright">{usd(tradeUsd(t))}</span>
						</div>
					))}
				</>
			)}
		</Section>
	);
}

const FLAGS_LIMIT = 50;

function FlaggedHere({ conditionId }: { conditionId: string }) {
	const api = useApiStatus();
	const { data, error, isLoading } = useSWR(api.state === "up" ? ["api:alerts:market", conditionId] : null, () =>
		getJson<AlertsResponse>("/alerts", { conditionId, limit: FLAGS_LIMIT }),
	);
	const all = useMemo(() => (data?.data ?? []).map((a) => toFlaggedRow(a)), [data]);
	const id = conditionId.toLowerCase();
	const rows = all.filter((r) => r.conditionId?.toLowerCase() === id);
	// An API without the filter answers with the general feed; rows from other markets give it away.
	// Then "none" would be a guess, so say what was actually checked.
	const partial = rows.length < all.length;
	const total = partial ? null : (data?.pagination.total ?? null);
	const note = total && total > rows.length ? `latest ${rows.length} of ${count(total)} · a flag is not proof` : "a flag is not proof";
	return (
		<Section title="Flagged positions" note={note}>
			{api.state === "down" ? (
				<Empty>Flags come from api.polygains.com, which is not responding.</Empty>
			) : error && !data ? (
				<Empty>Flags could not be checked for this market.</Empty>
			) : isLoading || api.state === "checking" ? (
				<SkeletonRows rows={2} cols={3} />
			) : rows.length === 0 ? (
				<Empty>
					{partial
						? FLAGS_REBUILDING ? "Flags are being rebuilt, so none can be shown yet." : `Not among the ${count(all.length)} most recent flags. Older flags were not checked.`
						: "No flagged positions on this market."}
				</Empty>
			) : (
				<>
					{rows.map((r) => (
						<div key={r.id} className="grid h-(--row-h) grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-2 border-b border-line/50 px-2 text-body last:border-b-0">
							<span className="truncate"><WalletLink address={r.wallet} label={shortAddr(r.wallet)} /></span>
							<OutcomeTag name={r.outcome} />
							<span className="num font-bold text-bright">{usd(r.volume)}</span>
							<span className="num text-dim">{stamp(r.time)}</span>
						</div>
					))}
					{partial ? <p className="px-2 py-1 text-label text-mute">Only the {count(all.length)} most recent flags were checked.</p> : null}
				</>
			)}
		</Section>
	);
}

function HolderSide({ name, side }: { name: string; side: OutcomeHolders }) {
	const split = holderSplit(side.holders.map((h) => h.amount));
	const top = [...side.holders].sort((a, b) => b.amount - a.amount).slice(0, 10);
	return (
		<Section title={`${name} holders`} note={`top ${side.holders.length} returned · amounts in shares`}>
			<div className="px-2 py-1.5">
				<div className="flex h-2 gap-0.5">
					{split.map((s, i) => (
						<span key={s.label} className={SPLIT_COLOR[i]} style={{ flexGrow: Math.max(s.share, 0.002), flexBasis: 0 }} title={`${s.label}: ${(s.share * 100).toFixed(1)}%`} />
					))}
				</div>
				<div className="num mt-1 flex flex-wrap gap-x-3 text-label text-dim">
					{split.map((s, i) => (
						<span key={s.label} className="flex items-center gap-1">
							<span aria-hidden="true" className={`h-1.5 w-1.5 ${SPLIT_COLOR[i]}`} />
							{s.label} <span className="text-bright">{(s.share * 100).toFixed(1)}%</span>
						</span>
					))}
				</div>
			</div>
			{top.map((h, i) => (
				<div key={h.proxy_wallet} className="grid h-(--row-h) grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-2 border-t border-line/50 px-2 text-body">
					<span className="num text-mute">{i + 1}</span>
					<span className="truncate"><WalletLink address={h.proxy_wallet} label={h.name || h.pseudonym} /></span>
					<span className="num text-bright" title="Shares held">{count(Math.round(h.amount))}</span>
				</div>
			))}
		</Section>
	);
}

function Holders({ market, conditionId }: { market: DeskMarket; conditionId: string }) {
	const { data, error, isLoading, mutate } = useLive(["pm:holders", conditionId], (signal) => fetchHolders(conditionId, undefined, signal));
	if (error && !data) return <div className="border border-line bg-panel"><SourceError source="Polymarket (data-api)" onRetry={() => void mutate()} /></div>;
	if (isLoading) {
		return (
			<div className="grid gap-2 md:grid-cols-2">
				{[0, 1].map((i) => <Section key={i} title="Holders"><SkeletonRows rows={10} cols={2} /></Section>)}
			</div>
		);
	}
	if (!data?.length) return <div className="border border-line bg-panel"><Empty>No holder data for this market.</Empty></div>;
	return (
		<div className="space-y-2">
			<p className="text-label text-mute">Split is among the largest holders Polymarket returns, not all open interest.</p>
			<div className="grid gap-2 md:grid-cols-2">
				{data.map((side) => {
					const name = market.outcomes.find((o) => o.tokenId === side.token_id)?.name ?? "Outcome";
					return <HolderSide key={side.token_id} name={name} side={side} />;
				})}
			</div>
		</div>
	);
}

function Charts({ conditionId }: { conditionId: string }) {
	const api = useApiStatus();
	const up = api.state === "up";
	const { data: status } = useSWR(up ? ["superset-status"] : null, fetchSupersetStatus);
	const { data: charts, error } = useSWR(status?.configured ? ["superset-charts"] : null, () => fetchSupersetCharts("market"));
	const ordered = useMemo(
		() => CHART_IDS.map((id) => charts?.find((c) => c.id === id)).filter((c) => c !== undefined),
		[charts],
	);
	const frame = "border border-line bg-panel";
	if (api.state === "down") return <div className={frame}><ApiDown what="trader composition, ownership and wallet-age charts" /></div>;
	if (status && !status.configured) return <div className={frame}><Empty>Charts are not configured on this API.</Empty></div>;
	if (error) return <div className={frame}><SourceError source="the charts service" /></div>;
	if (!charts) return <div className={frame}><SkeletonRows rows={8} cols={2} /></div>;
	return (
		<Suspense fallback={<div className={frame}><SkeletonRows rows={8} cols={2} /></div>}>
			<div className="grid gap-2 md:grid-cols-2">
				{ordered.map((chart) => <ChartCard key={chart.id} chart={chart} marketId={conditionId} />)}
			</div>
		</Suspense>
	);
}

/** Keeps Tab inside the dialog and hands focus back to whatever opened it. */
function useDialogFocus(panel: { current: HTMLElement | null }) {
	useEffect(() => {
		const opener = document.activeElement as HTMLElement | null;
		panel.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			const el = panel.current;
			if (e.key !== "Tab" || !el) return;
			const items = [...el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, [tabindex="0"]')];
			const first = items[0];
			const last = items[items.length - 1];
			if (!first || !last) return;
			const at = document.activeElement;
			if (e.shiftKey && (at === first || at === el)) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && at === last) {
				e.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("keydown", onKey);
			if (opener?.isConnected) opener.focus();
		};
	}, [panel]);
}

/** One plain sentence with the figures the market is ranked on. */
function whyLine(m: DeskMarket): string | null {
	if (m.closed) return null;
	const parts: string[] = [];
	if (m.volX !== null) parts.push(`24h volume is ${m.volX.toFixed(1)}× its own daily average`);
	else if (m.ageDays !== null) parts.push("Too new to compare with its own daily average");
	if (m.change24) parts.push(`price moved ${centsDelta(m.change24)} in 24h`);
	return parts.length ? `${parts.join("; ")}.` : null;
}

function DrawerHead({ market, conditionId, isLoading }: { market: DeskMarket | null; conditionId: string; isLoading: boolean }) {
	const { has, toggle } = useWatchlist();
	const pinned = has(conditionId);
	const why = market ? whyLine(market) : null;
	return (
		<div className="shrink-0 border-b border-line px-3 pt-2 pb-2">
			<div className="flex items-start justify-between gap-3">
				<div className="min-h-9 min-w-0 flex-1">
					{market ? (
						<h2 id="drawer-title" className="clamp-2 text-title font-bold text-bright" title={market.question}>{market.question}</h2>
					) : isLoading ? (
						<div aria-hidden="true" className="space-y-1.5 pt-1"><div className="skeleton-bar h-3 w-4/5" /><div className="skeleton-bar h-3 w-2/5" /></div>
					) : (
						<h2 id="drawer-title" className="truncate text-body text-dim">{conditionId}</h2>
					)}
				</div>
				<button type="button" onClick={closeMarket} aria-label="Close market" className="ctl shrink-0 max-sm:w-10 max-sm:px-0">
					<span aria-hidden="true" className="sm:hidden">✕</span>
					<span className="max-sm:hidden">close <span className="text-mute">esc</span></span>
				</button>
			</div>
			{why ? <p className="mt-1 text-body text-fg">{why}</p> : null}
			<div className="mt-1.5 flex min-h-(--ctl-h) flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
				<span className="num min-w-0 text-body text-dim">
					{market ? (
						<>
							{market.category ? `${market.category} · ` : ""}
							{market.closed ? "closed" : "resolves"} {isoDay(market.endDate)}
						</>
					) : null}
				</span>
				{market ? (
					<span className="flex gap-1.5">
						<Toggle on={pinned} onClick={() => toggle({ conditionId, question: market.question, addedPrice: market.outcomes[0]?.price ?? null, outcome: market.outcomes[0]?.name })}>
							{pinned ? "✓ watching" : "+ watch"}
						</Toggle>
						<a href={marketUrl(market.slug, conditionId)} target="_blank" rel="noreferrer" className="ctl">
							polymarket ↗
						</a>
					</span>
				) : null}
			</div>
		</div>
	);
}

function Figures({ market }: { market: DeskMarket }) {
	return (
		<StatGrid className="grid-cols-6 sm:grid-cols-5">
			<Stat className="col-span-2 sm:col-span-1" label="24h move" value={centsDelta(market.change24)} tone={toneClass(market.change24)} title="Change in the first outcome's price over 24h" />
			<Stat className="col-span-2 sm:col-span-1" label="Vol 24h" value={usd(market.vol24)} />
			<Stat className="col-span-2 sm:col-span-1" label="vs own avg" value={market.volX === null ? "–" : `${market.volX.toFixed(1)}×`} title="24h volume divided by this market's own recent daily average" />
			<Stat className="col-span-3 sm:col-span-1" label="Vol total" value={usd(market.volTotal)} />
			<Stat className="col-span-3 sm:col-span-1" label="Liquidity" value={usd(market.liquidity)} />
		</StatGrid>
	);
}

function OverviewSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-2">
			<div className="h-[43px] border border-line bg-panel" />
			<Section title="Outcomes"><div className="h-[58px] px-2 py-2"><div className="skeleton-bar mt-8 h-2" /></div></Section>
			<Section title="Recent large trades"><SkeletonRows rows={6} cols={3} /></Section>
		</div>
	);
}

export function MarketDrawer({ conditionId, closing = false }: { conditionId: string; closing?: boolean }) {
	const [tabParam, setTab] = useParam("mt", "overview");
	const tab = TABS.find((t) => t.id === tabParam)?.id ?? "overview";
	const panel = useRef<HTMLElement>(null);
	const { data, error, isLoading, mutate } = useLive(["pm:market", conditionId], (signal) => fetchMarketsByCondition([conditionId], signal), { refreshMs: 60_000 });
	const market = data?.[0] ? normalizeMarket(data[0]) : null;

	useDialogFocus(panel);

	return (
		<div className={closing ? "drawer-closing" : undefined}>
			<div className="drawer-scrim fixed inset-0 z-40 bg-black/60" onClick={closeMarket} aria-hidden="true" />
			<aside
				ref={panel}
				role="dialog"
				aria-modal="true"
				aria-labelledby="drawer-title"
				tabIndex={-1}
				className="drawer-panel fixed inset-0 z-50 flex flex-col bg-ink focus:outline-none sm:left-auto sm:w-full sm:max-w-[780px] sm:border-l sm:border-line"
			>
				<DrawerHead market={market} conditionId={conditionId} isLoading={isLoading} />
				<div className="shrink-0 border-b border-line px-2 py-1">
					<Tabs items={TABS} value={tab} onChange={(id) => setTab(id, true)} label="Market sections" />
				</div>
				<div role="tabpanel" className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-3">
					{error && !data ? (
						<div className="border border-line bg-panel"><SourceError source="Polymarket (gamma-api)" onRetry={() => void mutate()} /></div>
					) : !market ? (
						isLoading ? <OverviewSkeleton /> : <div className="border border-line bg-panel"><Empty>Market not found on Polymarket.</Empty></div>
					) : tab === "overview" ? (
						<>
							<Outcomes market={market} conditionId={conditionId} />
							<Figures market={market} />
							<ScoreWhy conditionId={conditionId} />
							<RecentLarge conditionId={conditionId} />
							<FlaggedHere conditionId={conditionId} />
						</>
					) : tab === "holders" ? (
						<Holders market={market} conditionId={conditionId} />
					) : (
						<Charts conditionId={conditionId} />
					)}
				</div>
			</aside>
		</div>
	);
}
